import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import '@patternfly/react-core/dist/styles/base.css';
import {
  Skeleton
} from '@patternfly/react-core';
import {
  Select,
  SelectDirection,
  SelectOption,
  SelectVariant
} from '@patternfly/react-core/deprecated';

/**
 *
 * @typedef SelectorItem
 * @type {object}
 * @property {string} name - Text that will be rendered
 * @property {string} description - Text that will be rendered as a small subtext on the item
 * @property {boolean} isDisabled - Decides if the item is disabled or not
 * @property {...*} rest - All other properties of the item will be stored and also be returned
 */

/**
 *
 * @callback onSelect
 * @param {SelectorItem} selectedItem - Item that is selected in Selector
 */

/**
 * Selector renders a dropdown box with loading skeleton. The item should conform to
 * {@link SelectorItem} but all extra parameters will not be touched and returned as is on
 * the selected item.
 *
 * @param {SelectorItem[]} items - Items to be rendered
 * @param {string} selectedItem - Item that shall be selected initial
 * @param {string} placeholderText - Placeholder text that is shown when no item is selected
 * @param {boolean} isLoading - Show loading skeleton
 * @param {onSelect} onSelect - Function called when item is selected
 *
 */
const Selector = ({ items, selectedItem, placeholderText = 'Select Item', isLoading = false, onSelect }) => {
  const [selected, setSelected] = useState(selectedItem);
  const [isOpen, setIsOpen] = useState(false);

  const _clearSelection = useCallback(() => {
    setSelected(null);
    setIsOpen(false);
  }, []);

  const _onSelect = useCallback((unusedEvent, selection, isPlaceholder) => {
    if (isPlaceholder) {
      _clearSelection();
    } else {
      setSelected(selection);
      setIsOpen(!isOpen);
      onSelect(items.filter((item) => item.name === selection));
    }
  }, [_clearSelection, isOpen, items, onSelect]);

  useEffect(() => {
    setSelected(selectedItem);
  }, [selectedItem]);

  if (isLoading) {
    return <Skeleton width="100%" screenreaderText="Loading contents" />;
  }


  return (
    <>
      <Select
        placeholderText={placeholderText}
        variant={SelectVariant.typeahead}
        aria-label="Select option"
        onToggle={(_event, isOpen) => setIsOpen(isOpen)}
        onSelect={_onSelect}
        isOpen={isOpen}
        direction={SelectDirection.down}
        menuAppendTo={'parent'}
        selections={selected}
      >
        { items.map((item, index) => {
          const { name, isDisabled, description } = item;
          return (<SelectOption key={index} isDisabled={isDisabled} value={name} description={description}/>);
        }) }
      </Select>
    </>
  );

};

// https://medium.com/@MoeSattler/better-prop-validation-in-react-cc83590d311f
Selector.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      isDisabled: PropTypes.bool,
      description: PropTypes.string
    })),
  selectedItem: PropTypes.string,
  isLoading: PropTypes.bool,
  onSelect: PropTypes.func,
  placeholderText: PropTypes.string
};

export default React.memo(Selector);
